"use client";
import {
  fetchAlgoliaProduct,
  listSlug,
  sortList,
} from "@/helpers/shoppingListHelper";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { setActiveCartList, updateCartListByStore } from "@/utils/redux/cartProduct";
import { fetchCartProducts } from "@/utils/redux/cartProduct/actions";
import { fetchFavouriteProducts } from "@/utils/redux/favouriteProduct/actions";
import {
  resetManageList,
  setGuestListAdded,
  setUserData,
  setUsersManageList,
  updateManageListByStore,
} from "@/utils/redux/manageList";
import { updateListActiveStatus } from "@/utils/redux/manageList/actions";
import {
  resetShoppingList,
  setShoppingListData,
  updateShoppingListByStore,
} from "@/utils/redux/shoppingList";
import { addAllCustomList } from "@/utils/redux/shoppingList/actions";
import { cartProduct } from "@/utils/types/manageList";
import { SingleItem } from "@/utils/types/product";
import { Box, Button, Select, Text } from "@dxp-feed/ui";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useGigya } from "@dxp-feed/gigya";
import Loader from "@/components/Loader";
import { AvailableOffers } from "./_components/AvailableOffers";
import { AutomaticAddedOffers } from "./_components/AutomaticAddedOffers";
import { offersRemovedFromStorage } from "@/utils/redux/favOffers";
import { CartAddedProducts } from "./_components/CartAddedProducts";
import { useStoreDetails } from "@/utils/hooks/useStoreDetails";
import { NO_ALCOHOL_STORES } from "@/utils/constant";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";

interface CartPanelProps {
  productCount: number;
  countHandler: (count: number | ((prev: number) => number)) => void;
  setIsPanelOpen?: (isOpen: boolean) => void;
  sidePanelOpen?: boolean;
  showOffers?: boolean;
}

const CartSidePanel = ({
  productCount,
  setIsPanelOpen,
  countHandler,
  sidePanelOpen,
  showOffers,
}: CartPanelProps) => {
  const [selectedValue, setSelectedValue] = useState("");
  const [activeList, setActiveList] = useState("");
  const [listData, setListData] = useState([]);
  const [products, setProducts] = useState<Record<string, any[]>>({});
  const [sortingType, setSortingType] = useState("byDepartment");
  const { gigya, isLoaded } = useGigya();
  const [loader, setLoader] = useState(false);
  const dispatch = useAppDispatch();
  const [offerCountInEmailJourney, setOfferCountInEmailJourney] =
    useState<number>(0);

  const { storeID, storeRegion } = useStoreDetails();

  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );
  const shoppingList: Record<string, any> = useAppSelector(
    (state) => state?.shoppingListSlice
  );
  const cartList: Record<string, any> = useAppSelector(
    (state) => state?.cartProductSlice
  );
  const {
    loggedIn: userLoggedIn,
    uid: uid,
    scene,
  } = useAppSelector((state) => state.gigyaslice);
  const { favOffers, offersToBeAdded, isLoading } = useAppSelector(
    (state) => state.favoirateOffersSlice
  );
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const isHash = [...searchParams.keys()].includes("hash");
  const checkPath = pathName.includes("manage-list") && pathName.split("/")[2];
  const slug = listSlug(
    `${encodeURIComponent(!userLoggedIn ? activeList : cartList?.listName)}_${!userLoggedIn ? selectedValue : cartList?.listId}`
  );
  useEffect(() => {
    if (storeID) {
      // update item data with new store id
      const updatedCartItems = cartList?.cartProducts?.products?.map((item: { item_id: string; }) => ({
        ...item,
        item_id: `${item.item_id?.split('_').slice(0, 2).join('_')}_${storeID}`
      }));
      if (updatedCartItems) {
        dispatch(updateCartListByStore(updatedCartItems));
      }


      const updatedManageListItems = {
        ...managementListData,
        itemsList: managementListData?.itemsList?.map((category: { items: any[]; }) => ({
          ...category,
          items: category?.items?.map(item => ({
            ...item,
            item_id: `${item?.item_id?.split('_').slice(0, 2).join('_')}_${storeID}`
          })) ?? []
        }))
      }
      if (updatedManageListItems) {
        dispatch(updateManageListByStore(updatedManageListItems));
      }
      const updatedShoppingItems = shoppingList?.shoppingListProducts?.products?.map((item: { item_id: string; }) => ({
        ...item,
        item_id: `${item.item_id?.split('_').slice(0, 2).join('_')}_${storeID}`
      }));
      if (updatedShoppingItems) {
        dispatch(updateShoppingListByStore(updatedShoppingItems));
      }

    }
  }, [storeID])

  const maxLength = 32;
  useEffect(() => {
    const allListItems = managementListData?.itemsList
      ?.filter(({ name }: cartProduct) => name !== "Favourites")
      .map(({ id, name, is_active, sorting, ...rest }: cartProduct) => ({
        optionval: id,
        optionname: name?.length > maxLength ? `${name.slice(0, maxLength)}...` : name,
        isActive: is_active,
        sorting: sorting,
      }));

    setListData(allListItems ?? []);

    if (!userLoggedIn && allListItems) {
      setSelectedValue(allListItems[0]?.optionval);
      setActiveList(allListItems[0]?.optionname);
      setSortingType(allListItems[0]?.sorting);
    } else {
      const activeItem = allListItems?.find(
        (item: { isActive: number }) => item.isActive === 1
      );
      setSelectedValue(activeItem?.optionval);
      setActiveList(activeItem?.optionname);
      setSortingType(activeItem?.sorting);
    }
  }, [managementListData, userLoggedIn]);

  const saveDataAndLogin = async (userInfo: {
    uid: string;
    id_token: string;
  }) => {
    if (!pathName.includes("manage-list")) {
      dispatch(setUserData({ userId: uid, categoryId: "" }));
      dispatch(resetShoppingList());
    }
    if (
      managementListData.itemsList?.length &&
      managementListData?.itemsList?.[0]?.isAuthenticated === false
    ) {
      const updatedProductsData = managementListData.itemsList.map(
        (item: SingleItem) => ({
          ...item,
          // user_id: userInfo?.uid ?? uid,
        })
      );
      if (updatedProductsData && userInfo?.uid != "") {
        dispatch(resetManageList());
      }
      const updatedList = Array.isArray(updatedProductsData)
        ? [...updatedProductsData]
        : [];

      const mergedShoppingList = [
        ...(shoppingList?.shoppingListProducts?.products ?? []),
        ...(shoppingList?.shoppingListProducts?.custom ?? []),
      ];

      if (!updatedList[0]) {
        updatedList[0] = { items: [] };
      }

      if (!Array.isArray(updatedList[0].items)) {
        updatedList[0].items = [];
      }
      updatedList[0] = {
        ...updatedList[0],
        items: [...(updatedList[0].items || []), ...mergedShoppingList],
      };

      const saveResult = await dispatch(
        addAllCustomList({
          listArray: updatedList,
          userToken: userInfo.id_token,
        })
      );
      if (saveResult.type === "add_all_shopping_list/fulfilled") {
        dispatch(setGuestListAdded());
        if (pathName != "/manage-list") {
          dispatch(
            fetchFavouriteProducts({
              isActive: false,
              isFavourite: true,
              userToken: userInfo?.id_token,
              storeId: storeID,
            })
          );
        }
      }
    } else if (pathName != "/manage-list") {
      dispatch(
        fetchFavouriteProducts({
          isActive: false,
          isFavourite: true,
          userToken: userInfo?.id_token,
          storeId: storeID,
        })
      );
    }else {
      dispatch(setGuestListAdded());
    }
    if (pathName != "/manage-list") {
      await getActiveList(userInfo);
    }
  };

  const count =
    cartList?.cartProducts?.products?.reduce((acc: number, product: any) => {
      const quantity = product?.quantity ?? 0;
      return quantity > 0 ? acc + quantity : acc;
    }, 0) ?? 0;

  useEffect(() => {
    const offersCount = (favOffers?.favouriteOffers?.length ?? 0);
    countHandler(count + (userLoggedIn ? offersCount : offersToBeAdded?.length));
  }, [cartList?.cartProducts?.products, favOffers, userLoggedIn, offersToBeAdded]);

  useEffect(() => {
    (async () => {
      if (!userLoggedIn || !storeID) return;
      const response = await gigyaToken();
      if (response.idToken != "" && response.uid != "") {
        await saveDataAndLogin({
          id_token: response.idToken,
          uid: response.uid,
        });
      }
    })()
      .then()
      .catch();
  }, [userLoggedIn, storeID]);

  const getActiveList = async (userInfo: { uid: string; id_token: string }) => {
    const result = await dispatch(
      fetchCartProducts({
        isActive: true,
        isFavourite: false,
        userToken: userInfo?.id_token,
        storeId: storeID
      })
    );
    if (result) {
      if (pathName != "/manage-list") {
        dispatch(setUsersManageList(result?.payload ?? []));
      }
      const updatedShoppingList = {
        products: [
          ...(result.payload?.getListData?.[0]?.items ?? []).filter(
            (val: { type: string }) => val.type === "Product"
          ),
        ],
        custom: [
          ...(result.payload?.getListData?.[0]?.items ?? []).filter(
            (val: { type: string }) => val.type === "Custom"
          ),
        ],

        listName: result.payload?.getListData[0]?.name || "",
        listId: result.payload?.getListData[0]?.id || null,
        is_active: result.payload?.getListData[0]?.is_active || false,
        isAuthenticated: true,
        updatedAt: result.payload?.getListData[0]?.updatedAt || new Date(),
      };
      if (!pathName.includes("manage-list")) {
        dispatch(resetManageList());
        dispatch(setUsersManageList(result.payload ?? []));
        dispatch(setShoppingListData(updatedShoppingList));
      }
    }
  };

  const fetchProducts = async () => {
    try {
      setLoader(true);
      if (cartList?.listId == selectedValue || !userLoggedIn) {
        const filteredProducts = cartList?.cartProducts?.products?.filter(
          (product: { quantity: number }) => (product?.quantity ?? 0) > 0
        );

        const result = await fetchAlgoliaProduct(
          filteredProducts,
          storeID,
          storeRegion
        );

        // const articleNumbers = new Set(
        //   result?.map((p: { articleNumber: string }) => p?.articleNumber)
        // );

        // let count = cartList?.cartProducts?.products?.reduce(
        //   (sum: number, item: { item_id: string; quantity: number }) => {
        //     return articleNumbers.has(item?.item_id)
        //       ? sum + item?.quantity
        //       : sum;
        //   },
        //   0
        // );

        const sortedProducts = await sortList(
          result,
          sortingType ?? "byDepartment",
          cartList?.cartProducts?.products
        );
        setProducts(sortedProducts);
      }
    } catch (err) {
      console.log("Error", err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (sidePanelOpen) {
      fetchProducts();
    }
  }, [cartList?.cartProducts?.products.length, userLoggedIn, sidePanelOpen]);

  const handleSelectChange = async (value: string) => {
    if (userLoggedIn && uid) {
      setLoader(true);
      const data = {
        id: Number(value),
        userToken: "",
      };

      const userResponse = await gigyaToken();
      try {
        if (userResponse?.idToken && userResponse?.idToken != "") {
          data.userToken = userResponse.idToken;
          const response = await dispatch(updateListActiveStatus(data));
          if (response?.payload) {
            const updatedItem = response?.payload?.getListData?.items?.map((item: { item_id: string; }) => ({
              ...item,
              item_id: `${item.item_id}_${storeID}`
            }));
            const result = await fetchAlgoliaProduct(
              updatedItem,
              storeID,
              storeRegion
            );
            setActiveList(response?.payload?.getListData?.name);
            const newCount =
              response?.payload?.getListData?.items?.reduce(
                (acc: number, product: any) => acc + (product?.quantity || 1),
                0
              ) || 0;
            const offersCount = (favOffers?.favouriteOffers?.length ?? 0);
            countHandler(newCount + (userLoggedIn ? offersCount : offersToBeAdded?.length));

            const sortedProducts = await sortList(
              result,
              response?.payload?.getListData?.sorting ?? "byDepartment",
              updatedItem
            );
            setProducts(sortedProducts);
            response.payload.getListData.items = updatedItem;
            dispatch(setActiveCartList(response?.payload?.getListData));
            const updatedShoppingList = {
              products: [
                ...(updatedItem ?? []).filter(
                  (val: { type: string }) => val.type === "Product"
                ),
              ],
              custom: [
                ...(response.payload?.getListData?.items ?? []).filter(
                  (val: { type: string }) => val.type === "Custom"
                ),
              ],
              //
              userData: {
                user_id: response.payload?.getListData?.user_id || null,
                id: response.payload?.getListData?.id || null,
              },
              listName: response.payload?.getListData?.name || "",
              listId: response.payload?.getListData?.id || null,
              is_active: response.payload?.getListData?.is_active || false,
              isAuthenticated: true,
              updatedAt: response.payload?.getListData?.updatedAt || new Date(),
            };
            if (!pathName.includes("manage-list")) {
              dispatch(setShoppingListData(updatedShoppingList));
            }
            setSelectedValue(value);
            setLoader(false);
          }
        } else {
          console.log("No ID token found");
          setLoader(false);
        }
      } catch (err) {
        console.log("Error", err);
      } finally {
        setLoader(false);
        if (pathName == "/manage-list" && typeof window !== "undefined") {
          window.location.reload();
        }
      }
    }
  };

  const router = useRouter();
  const gigyaToken = useGigyaAccountInfo();

  const [availableOffers, setAvailableOffers] = useState<any>([]);
  const [automaticAddedOffers, setAutomaticAddedOffers] = useState<any>([]);

  useEffect(() => {
    if (favOffers && !isLoading) {
      setAvailableOffers(favOffers?.favouriteOffers);
      setAutomaticAddedOffers(favOffers?.basketBuilderOffers);
    }
    if (!userLoggedIn && isHash) {
      if (typeof window != "undefined") {
        const offers = sessionStorage.getItem("offers") || "[]";
        let existingOffers: any[] = JSON.parse(offers);
        setAvailableOffers(existingOffers);
      }
    }
  }, [favOffers, offersToBeAdded]);

  const deleteOffersFromStorage = (selectedOffer: any) => {
    const remainingOffers = offersToBeAdded.filter(
      (offer: any) => offer.offerId !== selectedOffer.offerId
    );
    sessionStorage.setItem("offers", JSON.stringify(remainingOffers));
    dispatch(offersRemovedFromStorage(remainingOffers));
  };
  const handleCartProductChange = (itemNumber: number = 0) => {
    if (itemNumber == 0) {
      setTimeout(() => {
        setLoader(false);
      }, 500);
    } else {
      setLoader(true);
    }
  };

  const hasValues =
    products &&
    Object.values(products).some(
      (array) => Array.isArray(array) && array.length > 0
    );

  return !loader ? (
    <Box as="div" className="h-screen">
      <Box
        as="div"
        className="absolute top-[77px] left-0 right-0 bg-brandWhite z-[2]"
      >
        <Box
          as="div"
          className={`py-3 px-6 [&_button]:h-[56px] max-2xs:[&_button]:text-body [&_button]:text-base [&_button]:text-grey900 border-y-[2px] border-grey350 flex items-center w-full`}
        >
          <Box
            as="div"
            className={`w-[calc(100%-120px)] 3xs:w-[calc(100%-140px)] xs:w-[calc(100%-154px)] ${cartList?.listName == "" && "cursor-not-allowed"}`}
          >
            <Select
              options={listData}
              onChange={handleSelectChange}
              defaultValue={selectedValue} // Ensure this matches one of the optionval values
              selectAriaLabel={`${selectedValue ? cartList?.listName : `Select an option`}`}
              defaultLabel="Choose an option"
              error={false}
              scroll={true}
              className={`[&>button]:py-0 [&>button]:!h-10 w-full ${cartList?.listName == "" && "[&>button]:pointer-events-none"}`}
              icon={
                <Text
                  as="span"
                  className="icon-down-arrow max-2xs:text-[10px] text-sm text-grey950"
                ></Text>
              }
              disabled={cartList?.listName == ""}
            />
          </Box>
          <Box as="div" className="pl-2 xs:pl-4 ml-auto min-w-[120px] 3xs:min-w-[140px] xs:min-w-[154px]">
            <Button
              variant="primary"
              className="font-normal  !text-body xs:!text-base py-[2px] !h-[37px] px-2 !text-brandWhite w-full hover:!text-brandSecondary focus:!text-brandSecondary"
              onClick={() => {
                router.push("/manage-list");
                window.scrollTo(0, 0);
                setIsPanelOpen?.(false);
              }}
              aria-label="Navigate to manage lists page"
              title="Manage Lists"
              aria-hidden={!sidePanelOpen}
              disabled={!sidePanelOpen}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  router.push("/manage-list");
                  window.scrollTo(0, 0);
                  setIsPanelOpen?.(false);
                }
              }}
              target="_self"
            >
              Manage Lists
            </Button>
          </Box>
        </Box>
      </Box>

      {sidePanelOpen && (
        <Box
          key={""}
          as="div"
          role="contentinfo"
          className="bg-grey100 h-screen pt-[145px] pb-[70px] overflow-y-scroll z-1"
          aria-hidden={!sidePanelOpen}
          tabIndex={sidePanelOpen ? 0 : -1}
        >
          {/* <Text as="div" className="text-base font-bold text-brandSecondary py-3 px-6">
                    {activeList}
                </Text> */}
          {products &&
            Object.keys(products).length > 0 &&
            Object.keys(products)?.map(
              (product: string, productIndex: number) => (
                <Box
                  key={product + productIndex}
                  as="div"
                  className="bg-brandWhite"
                >
                  {sortingType == "byDepartment" && (
                    <Text
                      as="div"
                      className="max-2xs:text-body text-base font-bold text-brandSecondary py-3 px-6"
                    >
                      {product}
                    </Text>
                  )}
                  {Array.isArray(products[product]) &&
                    products[product]?.map((item: any) => (
                      <Box key={item.articleNumber}>
                        <CartAddedProducts
                          item={item}
                          Uid={uid as string}
                          userLoggedIn={userLoggedIn}
                          onResponseProductChange={handleCartProductChange}
                          setIsPanelOpen={setIsPanelOpen}
                        />
                      </Box>
                    ))}
                </Box>
                // )
              )
            )}

          {listData?.length > 0 && !hasValues && (
            <Box as="div" className="bg-white">
              <Text
                as="div"
                className="text-base font-bold text-brandBlack py-3 px-6"
              >
                {"You currently do not have any products in your shopping list"}
              </Text>
            </Box>
          )}
          <Box as="div" className="bg-brandWhite">
            {availableOffers?.length > 0 && showOffers && (
              <AvailableOffers
                availableOffers={availableOffers}
                deleteOffersFromStorage={deleteOffersFromStorage}
              />
            )}
            {automaticAddedOffers?.length > 0 && userLoggedIn && showOffers && (
              <AutomaticAddedOffers
                automaticAddedOffers={automaticAddedOffers}
              />
            )}
          </Box>
        </Box>
      )}

      <Box
        as="div"
        className="flex bg-brandWhite shadow-[0_-3px_7px_0_rgba(0,0,0,0.28)] pt-3 px-6 !py-[12px] absolute bottom-0 left-0 right-0 h-[68.78px] z-[2]"
      >
        <Box
          as="div"
          className={`w-full flex items-center ${listData?.length == 0 && "cursor-not-allowed"}`}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            const target = e.currentTarget; 
            setTimeout(() => {
              if (!target.contains(document.activeElement)) {
                setIsPanelOpen?.(false);
              }
            }, 0);
          }}
        >
          {listData?.length !== 0 && (cartList?.listName || cartList?.listId) ? (
            <Button
              variant="primary"
              className={`${!cartList?.listName && !cartList?.listId && "pointer-events-none opacity-50"} font-normal !text-base py-[4px] !w-full`}
              onClick={() => {
                router.push(`/manage-list/${slug}`);
                setIsPanelOpen?.(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  router.push(`/manage-list/${slug}`);
                  setIsPanelOpen?.(false);
                }
              }}
              aria-label="Edit List"
              aria-disabled={
                listData?.length === 0 ||
                (!cartList?.listName && !cartList?.listId)
              }
              tabIndex={
                listData?.length === 0 ||
                (!cartList?.listName && !cartList?.listId)
                  ? -1
                  : 0
              }
            >
              Edit List
            </Button>
          ) : (
            <Box
              as="div"
              className="font-normal !text-base py-[5px] !w-full bg-grey300 text-black text-center pointer-events-none opacity-50 rounded-full"
              aria-hidden="true"
            >
              Edit List
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  ) : (
    <Box as="div">
      {/* {loader && cartList?.isLoading ? ( */}
      <Loader className="fixed inset-0 bg-[#fff] bg-opacity-70 z-[9999] flex items-center justify-center" />
      {/* ) : null} */}
    </Box>
  );
};

export default CartSidePanel;
